/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import { Grid } from "@mui/material";
import { usePostgresViewer } from "components/viewer/context";
import { usePropertyContractHelloSign } from "utils/property-contract-embedded-sign";
import { getBrowserIanaTimeZone } from "utils/date";
import { trimStr } from "utils/string";
import { CREATE_PROPERTY_CONTRACT, SAVE_PROPERTY_CONTRACT_PROGRESS, CANCEL_PROPERTY_CONTRACT } from "./mutations";
import SupervisorSign from "./supervisor-sign";
import PropertyManagerSend from "./property-manager-send";
import type { CommonStepProps } from "../step-props";

type Props = Pick<
CommonStepProps,
| "formik"
| "onGoToSuccess"
| "preloadedTeam"
| "signatureRequestId"
| "setSignatureRequestId"
| "submissionId"
| "setSubmissionId"
| "orgId"
>;

const SignSection: React.FC<Props> = (props) => {
  const {
    formik,
    onGoToSuccess,
    preloadedTeam,
    signatureRequestId,
    setSignatureRequestId,
    orgId,
  } = props;
  const [contractId, setContractId] = React.useState<string | null>(null);
  const viewer = usePostgresViewer();
  const isSupervisor = Boolean(viewer?.isSupervisor);

  const [createPropertyContract] = useMutation(CREATE_PROPERTY_CONTRACT);
  const [saveProgress] = useMutation(SAVE_PROPERTY_CONTRACT_PROGRESS);
  const [cancelPropertyContract] = useMutation(CANCEL_PROPERTY_CONTRACT);

  const propertyName: string = trimStr(formik.values.propertyName);
  const signer = formik.values.signer || {};
  const selfSigning = Boolean(signer.selfSigning) || signer.signerUserId === viewer?.id;
  const signerUser = preloadedTeam.find((user) => user.id === (signer.signerUserId || ""));
  const supervisorEmail = trimStr(signerUser?.email);
  const supervisorName = trimStr(signerUser?.fullName);

  const [open] = usePropertyContractHelloSign();

  const cancelIfNeeded = async () => {
    if (!signatureRequestId) return;
    try {
      await cancelPropertyContract({
        variables: { signatureRequestId },
      });
    } catch {
      // best-effort; ignore
    } finally {
      setSignatureRequestId?.(null);
    }
  };

  const ensureSubmissionId = async () => {
    if (props.submissionId) return;
    try {
      const valuesJson = JSON.stringify(formik.values || {});
      const res = await saveProgress({
        variables: {
          data: {
            stepIndex: 5,
            valuesJson,
          },
        },
      });
      const sid = res?.data?.savePropertyContractProgress?.submissionId || "";
      if (sid) props.setSubmissionId?.(sid);
    } catch {
      // best-effort
    }
  };

  const buildContractVariables = (embedded: boolean): Record<string, unknown> => {
    const signerTitle = trimStr((formik.values.signer || {}).title);

    // Dates (order/start/end) are computed server-side in the property's timezone
    // (property address → org address → this browser zone → Pacific). We only pass the
    // browser zone as a fallback rather than browser-local dates.
    const base: Record<string, unknown> = {
      valuesJson: JSON.stringify(formik.values),
      embedded,
      userId: trimStr(viewer?.id),
      signerTitle,
      timeZone: getBrowserIanaTimeZone(),
    };
    if (props.submissionId) (base as Record<string, unknown>).submissionId = props.submissionId;
    if (embedded) {
      base.signerName = trimStr(viewer?.fullName) || "Supervisor";
      base.signerEmail = trimStr(viewer?.email);
    } else {
      base.signerName = supervisorName;
      base.signerEmail = supervisorEmail;
    }
    return base;
  };

  const createRequest = async (embedded: boolean) => {
    await ensureSubmissionId();
    const base = buildContractVariables(embedded);
    const result = await createPropertyContract({ variables: { data: base } });
    const payload = result?.data?.createPropertyContract;
    if (!payload) throw new Error("Failed to create request");
    if (payload.id) setContractId(payload.id);
    return {
      signUrl: payload.signUrl,
      signatureRequestId: payload.signatureRequestId,
      id: payload.id,
    };
  };

  const isPMPath = !isSupervisor || !selfSigning;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        {isPMPath ? (
          <PropertyManagerSend
            propertyName={propertyName}
            supervisorEmail={supervisorEmail}
            onGoToSuccess={onGoToSuccess}
            createEmailRequest={async () => {
              const { signatureRequestId: id } = await createRequest(false);
              setSignatureRequestId?.(id || null);
              return { signatureRequestId: id };
            }}
            cancelIfNeeded={cancelIfNeeded}
          />
        ) : (
          <SupervisorSign
            open={open}
            createEmbeddedRequest={async () => {
              const { signUrl, signatureRequestId: id, id: cid } = await createRequest(true);
              setSignatureRequestId?.(id || null);
              return { signUrl, signatureRequestId: id, id: cid };
            }}
            cancelIfNeeded={cancelIfNeeded}
            contractId={contractId}
            orgId={orgId}
            propertyName={propertyName}
            onGoToSuccess={onGoToSuccess}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default SignSection;
